import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { jwtDecode } from 'jwt-decode';
import { refreshToken as refreshTokenService } from '../services/auth';
import { useTokenRefresh } from '../hooks/useTokenRefresh';

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  requirePasswordChange?: boolean;
  [key: string]: any;
}

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  requirePasswordChange: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateRequirePasswordChange: (value: boolean) => void;
  refreshToken: (silent?: boolean) => Promise<TokenResponse | null>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  requirePasswordChange: false,
  isLoading: true,
  login: async () => {},
  logout: () => {},
  updateRequirePasswordChange: () => {},
  refreshToken: async () => null,
});

// Listen for logout events from other tabs/windows
const handleLogoutEvent = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Enable proactive token refresh to prevent expiration
  useTokenRefresh();

  const refreshToken = useCallback(async (silent = false): Promise<TokenResponse | null> => {
    const currentRefreshToken = localStorage.getItem('refreshToken');
    if (!currentRefreshToken) {
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      const error = new Error('No refresh token available');
      if (!silent) throw error;
      return null;
    }

    try {
      const result = await refreshTokenService();
      
      if (!result) {
        const error = new Error('Token refresh failed');
        if (!silent) throw error;
        return null;
      }

      const { accessToken, refreshToken: newRefreshToken, user } = result;
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      setUser(user);
      setIsAuthenticated(true);
      setRequirePasswordChange(!!user.requirePasswordChange);
      
      return {
        accessToken,
        refreshToken: newRefreshToken,
        user
      };
    } catch (error: any) {
      
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      
      if (!location.pathname.includes('/login')) {
        navigate('/login', { 
          replace: true,
          state: { from: location.pathname }
        });
      }
      throw error;
    }
  }, [location, navigate]);

  const logout = useCallback(() => {
    // Clear token from local storage
    localStorage.removeItem('token');
    // Clear any other auth-related data
    localStorage.removeItem('user');
    sessionStorage.clear();
    // Clear user from state
    setUser(null);
    // Clear token from state
    setIsAuthenticated(false);
    setRequirePasswordChange(false);
    // Clear any cached API data
    localStorage.clear();
    navigate('/login');
  }, [navigate]);

  const setupTokenRefresh = useCallback((tokenResponse: TokenResponse) => {
    const { accessToken } = tokenResponse;
    try {
      // Clear any existing refresh timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      const decoded: any = jwtDecode(accessToken);
      const expiryTime = decoded.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;
      
      // If token is expired or will expire in less than 5 minutes, refresh immediately
      if (timeUntilExpiry <= 5 * 60 * 1000) {
        console.log('[Auth] Token expiring soon, refreshing immediately');
        refreshToken(true).catch(console.error);
        return;
      }
      
      // Set up refresh 5 minutes before expiry
      const refreshTime = Math.max(0, timeUntilExpiry - (5 * 60 * 1000));

      console.log(`[Auth] Scheduling token refresh in ${Math.round(refreshTime / 1000 / 60)} minutes`);

      refreshTimeoutRef.current = setTimeout(async () => {
        try {
          console.log('[Auth] Proactively refreshing token');
          const newToken = await refreshToken(true);
          if (newToken) {
            setupTokenRefresh(newToken);
          } else {
            // If silent refresh fails, try one more time with error throwing
            const forcedToken = await refreshToken(false);
            if (forcedToken) {
              setupTokenRefresh(forcedToken);
            } else {
              throw new Error('Token refresh failed');
            }
          }
        } catch (error) {
          console.error('[Auth] Token refresh failed, logging out');
          // Force logout on refresh failure
          logout();
        }
      }, refreshTime);
    } catch (error) {
      console.error('[Auth] Failed to decode token for refresh scheduling');
      // Try to refresh token immediately if we can't decode the current one
      refreshToken().catch(() => {
        // Silent catch - errors are already handled in refreshToken
      });
    }
  }, [refreshToken, logout]);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { accessToken, refreshToken: newRefreshToken, user: userData } = response.data;
      
      if (!accessToken || !newRefreshToken || !userData) {
        throw new Error('Invalid response from server');
      }

      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      setUser(userData);
      setIsAuthenticated(true);
      setRequirePasswordChange(userData.requirePasswordChange || false);

      setupTokenRefresh({ accessToken, refreshToken: newRefreshToken, user: userData });

      // Redirect based on role and password change requirement
      if (userData.requirePasswordChange) {
        navigate('/change-password');
      } else {
        const intendedPath = location.state?.from || '/utilities/dashboard';
        navigate(intendedPath, { replace: true });
      }
    } catch (error: any) {
      
      const errorMessage = error.response?.data?.message || 'Failed to login';
      throw new Error(errorMessage);
    }
  };

  const updateRequirePasswordChange = (value: boolean) => {
    setRequirePasswordChange(value);
  };

  useEffect(() => {
    // Add logout event listener
    window.addEventListener('auth:logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent);
    };
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const currentRefreshToken = localStorage.getItem('refreshToken');
        
        if (!token || !currentRefreshToken) {
          setIsLoading(false);
          return;
        }
        
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          try {
            // Get fresh user data instead of relying on token decode
            const userResponse = await api.get('/auth/me');
            const user = userResponse.data;
            
            setUser(user);
            setIsAuthenticated(true);
            setRequirePasswordChange(!!user.requirePasswordChange);
            setupTokenRefresh({ accessToken: token, refreshToken: currentRefreshToken, user });
          } catch (error) {
            // Invalid token or user data, try to refresh
            const refreshResult = await refreshToken();
            if (!refreshResult) {
              throw new Error('Failed to refresh token');
            }
          }
        }
      } catch (error) {
        
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [refreshToken, setupTokenRefresh]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        requirePasswordChange,
        isLoading,
        login,
        logout,
        updateRequirePasswordChange,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}